import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  DialogTitle,
  Dialog,
  DialogContent,
  Grid,
  Typography,
  Stack,
  Box,
} from "@mui/material";
import { FruitifyContainer } from "../../../shared-components/Containers";
import { useParams } from "react-router-dom";
import { withPageTracking } from "../../../services/postHog";
import { useAuthenticationStore } from "../../../stores/auth";
import {
  hasProcessingAnswers,
  useStudentAssignmentAnswers,
} from "../hooks/useStudentAssignmentAnswers";
import GeneralLoading from "../../../shared-components/Loading";
import { StudentSummaryAndQuickNav } from "./AnswerView";
import {
  AnswerViewer,
  StudentPerformanceSummaryStats,
} from "../components/AssignmentQuestionAnswerViewer";
import { AssignmentQuestionCard } from "../components/AssignmentQuestionCard";
import { ChevronLeft, PictureAsPdfOutlined } from "@mui/icons-material";
import { QuestionViewCard } from "../../question/components/QuestionViewCard";
import { AnswerStatus } from "../components/AnswerStatus";
import FruitifyIcon from "../../../shared-components/FruitifyIcons";
import { AnswerFileUploadDialog } from "../components/AnswerFileUploadDialog";
import { useAnswerFileUploader } from "../hooks/useAnswerFileUploader";
import { useApiClient } from "../../../api";
import { useSubmitHandwrittenFile } from "../hooks/useSubmitHandwrittenFile";
import { useSubmitAnswer } from "../hooks/useSubmitAnswer";
import { QRCodeSVG } from "qrcode.react";
import { enqueueSnackbar } from "notistack";
import { FileDropZone } from "../components/FileDropZone";
import { AwaitingFeedbackCard } from "../states/AwaitingFeedbackCard";
import { FeedbackEditor } from "../components/FeedbackEditor";
import { checkUserHasRole } from "../../auth/Utils";

const StudentAnswerFromPictureUploadDialog = ({
  open,
  setOpen,
  assignmentQuestion,
}) => {
  const ApiClient = useApiClient();
  const [uploadUrl, setUploadUrl] = useState(null);
  const generateUploadUrl = async () => {
    setUploadUrl(null);
    try {
      const response = await ApiClient.assignment.generateUploadURL({
        assignmentQuestion: assignmentQuestion.id,
        studentDirect: true,
      });
      if (response.success) {
        setUploadUrl(response.url);
      } else {
        enqueueSnackbar("Error Generating Upload URL- please try again!", {
          variant: "error",
          autoHideDuration: 3000,
        });
      }
    } catch (error) {
      console.error("Failed to Generate Upload URL", error);
      enqueueSnackbar("Error Generating Upload URL- please try again!", {
        variant: "error",
        autoHideDuration: 3000,
      });
    }
  };
  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (open) {
      generateUploadUrl();
    }
  }, [open]);
  return (
    <Dialog open={open} onClose={handleClose} fullWidth>
      <DialogTitle>Upload from camera</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Button
            variant="contained"
            startIcon={<FruitifyIcon name="Camera" />}
            component="label"
            onClick={() => {
              const currentUrl = window.location.href; // current page
              const url = new URL(uploadUrl); // your backend upload URL
              url.searchParams.set("redirect", currentUrl); // add redirect param
              window.location.href = url.toString();
            }}
          >
            Use this device
          </Button>
          <Box sx={{ mt: 3, textAlign: "center" }}>
            <Typography gutterBottom textAlign="center">
              Or scan this QR code with your phone
            </Typography>
            <Box my={1}>
              <QRCodeSVG value={uploadUrl} size={200} />
            </Box>
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
const FileUploadOptionsDialog = ({
  open,
  setOpen,
  student,
  assignmentQuestion,
  handleSubmit,
}) => {
  const ApiClient = useApiClient();
  const fileUploader = useAnswerFileUploader({
    independentUser: false,
    student: student,
  });
  const { submitHandwrittenFile } = useSubmitHandwrittenFile(
    assignmentQuestion,
    fileUploader,
    ApiClient,
  );

  const handleClose = () => {
    setOpen(false);
  };
  return (
    <Dialog open={open} onClose={handleClose} fullWidth>
      <AnswerFileUploadDialog
        studentView
        open={fileUploader.fileDialogOpen}
        setOpen={fileUploader.setFileDialogOpen}
        file={fileUploader.file}
        identifier={fileUploader.identifier}
        setIdentifier={fileUploader.setIdentifier}
        onSubmitProcessedAnswer={handleSubmit}
        onSubmitHandwrittenAnswer={submitHandwrittenFile}
      />
      <DialogTitle>Upload File</DialogTitle>
      <DialogContent>
        <FileDropZone
          files={[fileUploader.file]}
          onFilesChange={fileUploader.handleFileUpload}
        />
      </DialogContent>
    </Dialog>
  );
};

export const UploadAnswerButtons = ({ assignmentQuestion }) => {
  const [fileUploadOpen, setFileUploadOpen] = useState(false);
  const [imageUploadOpen, setImageUploadOpen] = useState(false);
  const { user } = useAuthenticationStore();
  const ApiClient = useApiClient();

  const { submitAnswer } = useSubmitAnswer(assignmentQuestion, ApiClient);

  const handleSubmit = async (answer, identifier) => {
    submitAnswer(answer, identifier);
    setFileUploadOpen(false);
    setImageUploadOpen(false);
  };

  return (
    <Grid container spacing={2} sx={{ width: "100%" }}>
      <FileUploadOptionsDialog
        open={fileUploadOpen}
        setOpen={setFileUploadOpen}
        assignmentQuestion={assignmentQuestion}
        handleSubmit={handleSubmit}
        student={user.info.student}
      />
      <StudentAnswerFromPictureUploadDialog
        open={imageUploadOpen}
        setOpen={setImageUploadOpen}
        assignmentQuestion={assignmentQuestion}
      />
      <Grid size={{ xs: 12, sm: 6 }}>
        <Button
          variant="contained"
          startIcon={<FruitifyIcon name="Camera" />}
          onClick={() => setImageUploadOpen(true)}
          fullWidth
        >
          Take a picture
        </Button>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <Button
          variant="contained"
          startIcon={<PictureAsPdfOutlined />}
          onClick={() => setFileUploadOpen(true)}
          fullWidth
        >
          Upload a document
        </Button>
      </Grid>
    </Grid>
  );
};

const StudentAnswerSubmissionAndViewer = ({
  assignmentQuestion,
  onBackToAssignment,
  answer,
  setAnswer,
  onSuccessfulSubmit,
  disableSubmit = false,
  showFeedback = false,
}) => {
  const [editing, setEditing] = useState(false);
  const [dirty, setDirty] = useState(false);
  return (
    <div>
      <Button
        variant={"text"}
        startIcon={<ChevronLeft />}
        onClick={() => {
          if (!dirty || window.confirm("Discard your unsaved typed answer?"))
            onBackToAssignment();
        }}
      >
        Back to assignment
      </Button>
      {showFeedback && (
        <StudentPerformanceSummaryStats
          question
          studentView
          availableMarks={assignmentQuestion.availableMarks}
          mark={answer?.teacherFeedback?.mark ?? 0}
          noAnswers={!answer}
        />
      )}
      <QuestionViewCard
        question={assignmentQuestion.question}
        identifier={assignmentQuestion.identifier}
        sx={{ mt: showFeedback ? 4 : 2 }}
      />
      {!disableSubmit && (
        <UploadAnswerButtons assignmentQuestion={assignmentQuestion} />
      )}
      {!showFeedback ? (
        <AwaitingFeedbackCard />
      ) : (
        <FeedbackEditor
          savedFeedback={answer?.teacherFeedback}
          student
          studentLoginView
        />
      )}
      <AnswerViewer
        onDirtyChange={setDirty}
        studentView
        studentAnswer={answer}
        setStudentAnswer={setAnswer}
        disableSubmit={disableSubmit}
        onEditClicked={() => setEditing(true)}
        onEditCancelled={() => setEditing(false)}
        editing={editing || !answer?.answer}
        onSuccessfulSubmit={onSuccessfulSubmit}
        showFeedback={showFeedback}
        assignmentQuestion={assignmentQuestion}
        student={answer?.student}
      />
    </div>
  );
};

const Overview = ({
  totalMarks,
  allAssignmentInfo,
  onQuestionClicked,
  disableSubmit = false,
  showFeedback = false,
}) => {
  const answers = allAssignmentInfo.answers || [];
  return (
    <>
      {showFeedback ? (
        <StudentPerformanceSummaryStats
          studentView
          hideTitle
          availableMarks={allAssignmentInfo.assignmentAvailableMarks}
          mark={totalMarks}
          noAnswers={allAssignmentInfo.answers.length === 0}
          lastAssignmentScore={allAssignmentInfo.lastAssignmentScore}
        />
      ) : (
        <AwaitingFeedbackCard />
      )}
      {[...allAssignmentInfo.assignment.assignmentQuestions]
        .sort((a, b) => a.identifier - b.identifier)
        .map((aq) => {
          const answer = answers.find(
            (ans) => ans.assignmentQuestion.id === aq.id,
          );

          const mark = answer?.teacherFeedback?.mark ?? null;

          return (
            <AssignmentQuestionCard
              studentView
              disableSubmit={disableSubmit}
              key={aq.id}
              assignmentQuestion={aq}
              sx={{ mt: 2 }}
              onClick={() => onQuestionClicked(aq)}
              StatusComponent={
                <AnswerStatus answer={answer} notSubmitted={!answer} />
              }
              mark={mark}
              availableMarks={aq.availableMarks}
            />
          );
        })}
    </>
  );
};

const StudentAssignmentView = () => {
  const { assignmentId } = useParams();
  const { user } = useAuthenticationStore();
  const [chosenQuestion, setChosenQuestion] = useState(null);
  const [answer, setAnswer] = useState(null);

  const {
    loading,
    error,
    allAssignmentInfo,
    mappedMarks,
    fetchStudentAnswers,
    pollDuringMarking,
  } = useStudentAssignmentAnswers(assignmentId);

  useEffect(() => {
    if (assignmentId && checkUserHasRole({ user: user, role: "student" })) {
      const loadAnswers = async () => {
        const result = await fetchStudentAnswers();
        if (hasProcessingAnswers(result?.answers)) pollDuringMarking();
      };
      loadAnswers();
    }
  }, [assignmentId, user]);

  const totalMarks = mappedMarks.reduce((sum, marksObj) => {
    return (
      sum + Object.values(marksObj).reduce((objSum, mark) => objSum + mark, 0)
    );
  }, 0);

  useEffect(() => {
    if (chosenQuestion && allAssignmentInfo) {
      const matchedAnswer = allAssignmentInfo.answers?.find(
        (a) => a.assignmentQuestion.id === chosenQuestion.id,
      );

      setAnswer(
        matchedAnswer || {
          assignmentQuestion: chosenQuestion,
          student: allAssignmentInfo.student,
          answer: "",
        },
      );
    } else {
      setAnswer(null);
    }
  }, [chosenQuestion, allAssignmentInfo]);

  if (error) {
    return <Alert>Unable to load data. {error}</Alert>;
  }

  if (loading || !allAssignmentInfo) {
    return <GeneralLoading />;
  }

  const showFeedback = allAssignmentInfo?.assignment.showFeedbackToStudent;

  return (
    <FruitifyContainer>
      <StudentSummaryAndQuickNav
        studentView
        lastSubmitted={allAssignmentInfo?.lastSubmitted}
        student={allAssignmentInfo.student}
      />
      {chosenQuestion ? (
        <StudentAnswerSubmissionAndViewer
          onBackToAssignment={() => setChosenQuestion(null)}
          disableSubmit={allAssignmentInfo?.assignment.disableSubmission}
          assignmentQuestion={chosenQuestion}
          answer={answer}
          setAnswer={setAnswer}
          onSuccessfulSubmit={() => {
            pollDuringMarking();
          }}
          showFeedback={showFeedback}
        />
      ) : (
        <Overview
          totalMarks={totalMarks}
          allAssignmentInfo={allAssignmentInfo}
          showFeedback={showFeedback}
          onQuestionClicked={(aq) => setChosenQuestion(aq)}
          disableSubmit={allAssignmentInfo?.assignment.disableSubmission}
        />
      )}
    </FruitifyContainer>
  );
};

export default withPageTracking(StudentAssignmentView, {
  feature: "assignments-view-student",
});
